import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import Consumables from './Consumables';
import { Scrap } from './ConfigsScrap';
import BarcodeScanner, { barcodeSupported } from '../components/BarcodeScanner';

// Only these device types carry a MAC address worth recording.
const MAC_TYPES = ['LAPTOP', 'DESKTOP', 'SERVER'];

// Map varied spreadsheet headers → normalized import keys
function mapAssetRow(raw) {
  const get = (...keys) => {
    for (const k of keys) {
      const found = Object.keys(raw).find(h => h.toLowerCase().replace(/[^a-z0-9]/g,'') === k);
      if (found && String(raw[found]).trim()) return String(raw[found]).trim();
    }
    return '';
  };
  return {
    asset_code:       get('assetcode','code','assettag','tag'),
    asset_type:       get('assettype','type'),
    brand:            get('brand','make'),
    model:            get('model'),
    serial_number:    get('serialnumber','serial','serialno','sno'),
    transaction_type: get('transactiontype','txntype','transaction'),
    campus:           get('campus','campusname'),
    block:            get('block','location'),      // Block = Location master (corridor/gate/etc)
    location:         get('locationcode','roomnumber','room','roomno','locationroom'),
    name:             get('name','username','assignedto'),
  };
}

// ─── Bulk Upload Modal ─────────────────────────────────────────────────────
function BulkUploadModal({ onClose, onDone }) {
  const [step,    setStep]    = useState('pick');   // pick → preview → result
  const [rows,    setRows]    = useState([]);
  const [fileName,setFileName]= useState('');
  const [result,  setResult]  = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name); setErr('');
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const wb = XLSX.read(evt.target.result, { type:'array' });
        // Prefer a sheet whose name suggests asset data, else first with most rows
        let best = null, bestCount = -1;
        wb.SheetNames.forEach(sn => {
          const arr = XLSX.utils.sheet_to_json(wb.Sheets[sn], { defval:'' });
          const score = /asset|data|clean/i.test(sn) ? arr.length + 100000 : arr.length;
          if (score > bestCount) { bestCount = score; best = arr; }
        });
        const mapped = (best || []).map(mapAssetRow).filter(r => r.asset_code);
        if (!mapped.length) { setErr('No rows with an Asset Code found. Check the file.'); return; }
        setRows(mapped); setStep('preview');
      } catch(ex) { setErr('Could not read file: ' + ex.message); }
    };
    reader.readAsArrayBuffer(file);
  };

  const doUpload = async () => {
    setBusy(true); setErr('');
    try {
      // Send in chunks to stay light
      const CHUNK = 2000;
      let agg = { inserted:0, skipped:0, roomsCreated:0, locsCreated:0, unmatchedCampuses:[], errors:[] };
      for (let i = 0; i < rows.length; i += CHUNK) {
        const r = await api.post('/assets/import', { rows: rows.slice(i, i+CHUNK) });
        const d = r.data;
        agg.inserted += d.inserted; agg.skipped += d.skipped;
        agg.roomsCreated += d.roomsCreated; agg.locsCreated += d.locsCreated;
        d.unmatchedCampuses?.forEach(u => { if (!agg.unmatchedCampuses.includes(u)) agg.unmatchedCampuses.push(u); });
      }
      setResult(agg); setStep('result');
    } catch(ex) { setErr(ex.response?.data?.error || 'Upload failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:640 }}>
        <div className="modal-header">
          <span className="modal-title">Bulk Upload Assets — Excel / CSV</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {step === 'pick' && (
          <>
            <div style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:16, fontSize:13, lineHeight:1.7 }}>
              <div style={{ fontWeight:600, marginBottom:6, color:'var(--text2)', fontSize:12, textTransform:'uppercase', letterSpacing:'0.05em' }}>Expected columns</div>
              <strong>Asset Code</strong> (required), Asset Type, Brand, Model, Serial Number, Transaction Type, <strong>Campus</strong>, <strong>Block</strong> (→ Location master), <strong>Location</strong> (→ Room number).<br/>
              <span style={{ color:'var(--text2)' }}>
                Rooms &amp; Locations are auto-created per campus. Transaction Type maps to status (Stock→In&nbsp;Stock, Scrap→Retired, Intra/Inter&nbsp;Campus→Active). Duplicate Asset Codes are skipped.
              </span>
            </div>
            <div className="form-group">
              <label>Select File (.xlsx / .csv)</label>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile}
                style={{ padding:'8px 0', background:'transparent', border:'none', color:'var(--text)' }} />
            </div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button></div>
          </>
        )}

        {step === 'preview' && (
          <>
            <div style={{ display:'flex', gap:16, marginBottom:14, alignItems:'center' }}>
              <div style={{ padding:'8px 16px', background:'rgba(79,142,247,0.1)', borderRadius:8, textAlign:'center' }}>
                <div style={{ fontSize:22, fontWeight:700, color:'var(--accent)' }}>{rows.length}</div>
                <div style={{ fontSize:11, color:'var(--text2)' }}>Assets to import</div>
              </div>
              <div style={{ fontSize:12, color:'var(--text2)' }}>{fileName}</div>
            </div>
            <div className="table-wrap" style={{ maxHeight:300, overflowY:'auto', marginBottom:14 }}>
              <table>
                <thead><tr><th>Code</th><th>Type</th><th>Brand/Model</th><th>Campus</th><th>Block</th><th>Room</th><th>Txn</th></tr></thead>
                <tbody>
                  {rows.slice(0, 50).map((r,i) => (
                    <tr key={i}>
                      <td className="mono" style={{ fontSize:11 }}>{r.asset_code}</td>
                      <td style={{ fontSize:11 }}>{r.asset_type||'—'}</td>
                      <td style={{ fontSize:11 }}>{r.brand} {r.model}</td>
                      <td style={{ fontSize:11 }}>{r.campus||'—'}</td>
                      <td style={{ fontSize:11 }}>{r.block||'—'}</td>
                      <td style={{ fontSize:11 }}>{r.location||'—'}</td>
                      <td style={{ fontSize:11 }}>{r.transaction_type||'—'}</td>
                    </tr>
                  ))}
                  {rows.length > 50 && <tr><td colSpan={7} style={{ textAlign:'center', color:'var(--text2)', fontSize:12, padding:8 }}>…and {rows.length-50} more</td></tr>}
                </tbody>
              </table>
            </div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setStep('pick')}>← Back</button>
              <button className="btn btn-primary" onClick={doUpload} disabled={busy}>{busy ? 'Importing…' : `Import ${rows.length} Assets`}</button>
            </div>
          </>
        )}

        {step === 'result' && result && (
          <>
            <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
              {[['Inserted', result.inserted, 'var(--green)'], ['Skipped', result.skipped, 'var(--amber)'],
                ['Rooms created', result.roomsCreated, 'var(--accent)'], ['Locations created', result.locsCreated, 'var(--accent)']].map(([l,v,c]) => (
                <div key={l} style={{ flex:'1 1 120px', padding:'14px', background:'var(--bg3)', borderRadius:10, textAlign:'center' }}>
                  <div style={{ fontSize:24, fontWeight:700, color:c }}>{v}</div>
                  <div style={{ fontSize:11, color:'var(--text2)' }}>{l}</div>
                </div>
              ))}
            </div>
            {result.unmatchedCampuses?.length > 0 && (
              <div style={{ background:'rgba(245,191,36,0.08)', border:'1px solid rgba(245,191,36,0.2)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:14, fontSize:12 }}>
                <div style={{ fontWeight:600, color:'var(--amber)', marginBottom:6 }}>Skipped — campus not found in portal:</div>
                {result.unmatchedCampuses.map((u,i) => <div key={i}>• {u}</div>)}
                <div style={{ marginTop:6, color:'var(--text2)' }}>Add these campuses in Master Data, then re-upload to import their assets.</div>
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setStep('pick')}>Upload Another</button>
              <button className="btn btn-primary" onClick={() => { onDone(); onClose(); }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const TYPES = ['Laptop','Desktop','Monitor','Printer','UPS','Switch','Router','Firewall',
  'Access Point','Server','Storage','CCTV NVR','Phone','Projector','Other'];
const CONDITIONS = ['Good','Fair','Needs Repair','Faulty'];
const STATUSES    = ['active','stock','sent_for_service','retired'];
const STATUS_LABELS = { active:'Active', stock:'In Stock', sent_for_service:'Sent for Service', retired:'Retired' };
const STATUS_COLORS = { active:'badge-completed', stock:'badge-discuss', sent_for_service:'badge-pending', retired:'badge-faulty' };

function condBadge(c) {
  const map = { Good:'badge-good', Fair:'badge-fair', 'Needs Repair':'badge-repair', Faulty:'badge-faulty' };
  return <span className={`badge ${map[c]||''}`}>{c}</span>;
}

// ─── Warranty helpers ──────────────────────────────────────────────────────
function computeExpiry(purchaseDate, months) {
  if (!purchaseDate || !months) return '';
  const d = new Date(purchaseDate);
  if (isNaN(d)) return '';
  d.setMonth(d.getMonth() + parseInt(months));
  return d.toISOString().slice(0, 10);
}

function warrantyInfo(expiry) {
  if (!expiry) return { status:'No Warranty', color:'var(--text2)', remaining:'—' };
  const exp = new Date(expiry), now = new Date();
  const diff = exp - now;
  if (diff <= 0) return { status:'Expired', color:'var(--red)', remaining:'0 days' };
  let days = Math.ceil(diff / 86400000);
  const y = Math.floor(days / 365); days -= y * 365;
  const m = Math.floor(days / 30);  days -= m * 30;
  const parts = [];
  if (y) parts.push(`${y} Year${y>1?'s':''}`);
  if (m) parts.push(`${m} Month${m>1?'s':''}`);
  if (days) parts.push(`${days} Day${days>1?'s':''}`);
  return { status:'Active', color:'var(--green)', remaining: parts.join(', ') || 'Today' };
}

function fmtRemaining(days) {
  if (days === null || days === undefined) return '—';
  if (days <= 0) return 'Expired';
  let d = days;
  const y = Math.floor(d / 365); d -= y * 365;
  const m = Math.floor(d / 30);  d -= m * 30;
  const parts = [];
  if (y) parts.push(`${y}Y`);
  if (m) parts.push(`${m}M`);
  if (d) parts.push(`${d}D`);
  return parts.join(' ') || 'Today';
}

// Working-days helper for the expected-return PREVIEW only — the date actually
// stored is calculated on the server, which also knows the holidays table.
// Sunday is the only weekly holiday here; Saturday is a working day.
function addWorkingDaysClient(start, days) {
  const [y, m, dd] = String(start).slice(0, 10).split('-').map(Number);
  const d = new Date(y, m - 1, dd);   // local midnight, so no timezone shift
  let added = 0;
  while (added < days) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0) added++; }
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ─── Send for Service Modal ────────────────────────────────────────────────
function SendServiceModal({ asset, onSave, onClose, busy }) {
  const [form, setForm] = useState({ service_out_date: new Date().toISOString().slice(0,10),
    vendor_id:'', vendor_job_no:'', service_type:'Out of Warranty', problem_desc:'', priority:'Medium', remarks:'' });
  const [vendors, setVendors] = useState([]);
  const [err, setErr] = useState('');
  const wt = warrantyInfo(asset.warranty_expiry);

  useEffect(() => { api.get('/master/vendors').then(r => setVendors(r.data)).catch(()=>{}); }, []);
  const vendor = vendors.find(v => String(v.id) === String(form.vendor_id));
  const expectedReturn = vendor?.sla_days && form.service_out_date ? addWorkingDaysClient(form.service_out_date, vendor.sla_days) : null;

  const save = () => {
    if (!form.vendor_id) { setErr('Select a vendor'); return; }
    if (!form.problem_desc.trim()) { setErr('Problem description is required'); return; }
    onSave(form);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:540 }}>
        <div className="modal-header">
          <span className="modal-title">Send to Service — <span className="mono" style={{ color:'var(--accent)' }}>{asset.asset_tag}</span></span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <p style={{ fontSize:13, color:'var(--text2)', marginBottom:14 }}>
          <strong>{asset.brand} {asset.model}</strong> · {asset.asset_type}
        </p>

        <div style={{ background:'var(--bg3)', borderRadius:'var(--radius)', padding:'10px 12px', marginBottom:14, fontSize:12, display:'flex', justifyContent:'space-between' }}>
          <span style={{ color:'var(--text2)' }}>Warranty at service time:</span>
          <span style={{ color: wt.color, fontWeight:600 }}>{wt.status}{wt.status==='Active' ? ` · ${wt.remaining} left` : ''}</span>
        </div>

        <div className="form-row">
          <div className="form-group"><label>Service Out Date</label>
            <input type="date" value={form.service_out_date} onChange={e => setForm(f => ({ ...f, service_out_date: e.target.value }))} /></div>
          <div className="form-group"><label>Vendor *</label>
            <select value={form.vendor_id} onChange={e => setForm(f => ({ ...f, vendor_id: e.target.value }))}>
              <option value="">— Select vendor —</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            {!vendors.length && <div style={{ fontSize:11, color:'var(--amber)', marginTop:4 }}>No vendors yet — add them under Master Data → Vendors.</div>}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Vendor Job / Ref No. <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span></label>
            <input value={form.vendor_job_no} onChange={e => setForm(f => ({ ...f, vendor_job_no: e.target.value }))} placeholder="vendor's reference" /></div>
          <div className="form-group"><label>Service Type</label>
            <select value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))}>
              <option>In Warranty</option><option>Out of Warranty</option><option>AMC</option>
            </select></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Priority</label>
            <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
              <option>Critical</option><option>High</option><option>Medium</option><option>Low</option>
            </select></div>
          <div className="form-group"><label>Expected Return <span style={{ fontSize:11, color:'var(--text2)' }}>(SLA auto)</span></label>
            <input disabled value={expectedReturn ? new Date(expectedReturn).toLocaleDateString() + ` · ${vendor.sla_days} wd` : '—'} /></div>
        </div>
        <div className="form-group"><label>Problem Description *</label>
          <textarea rows={2} value={form.problem_desc} onChange={e => setForm(f => ({ ...f, problem_desc: e.target.value }))} placeholder="Describe the fault…" /></div>
        <div className="form-group"><label>Remarks <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span></label>
          <input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} placeholder="first timeline note" /></div>

        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Sending…' : 'Send to Vendor'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Update Service Status Modal ───────────────────────────────────────────
function UpdateServiceModal({ asset, onSave, onClose, busy }) {
  const [form, setForm] = useState({
    service_type:'', service_report:'', completion_date: new Date().toISOString().slice(0,10), vendor:'',
    replaced_part_name:'', part_serial_number:'', part_warranty_months:'', service_charge:''
  });
  const [err, setErr] = useState('');

  const save = () => {
    if (!form.service_type) { setErr('Select a service type'); return; }
    if (!form.service_report.trim()) { setErr('Service report is required'); return; }
    if (form.service_type === 'Physical Damage' && !form.replaced_part_name.trim()) {
      setErr('Replaced part name is required for physical damage'); return;
    }
    onSave(form);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <div className="modal-header">
          <span className="modal-title">Update Service — <span className="mono" style={{ color:'var(--accent)' }}>{asset.asset_tag}</span></span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <p style={{ fontSize:13, color:'var(--text2)', marginBottom:14 }}>
          <strong>{asset.brand} {asset.model}</strong> · {asset.asset_type}. On close, asset returns to <strong>Stock</strong> with condition <strong>Good</strong>.
        </p>

        {/* Service type */}
        <div className="form-group">
          <label>Service Type *</label>
          <div style={{ display:'flex', gap:16 }}>
            {['Warranty Claim','Physical Damage'].map(t => (
              <label className="opt" key={t} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', textTransform:'none', letterSpacing:0, fontSize:13 }}>
                <input type="radio" name="svctype" checked={form.service_type===t} onChange={() => setForm(f => ({ ...f, service_type:t }))} style={{ width:15, height:15 }} />
                {t}
              </label>
            ))}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Service Completion Date</label>
            <input type="date" value={form.completion_date} onChange={e => setForm(f => ({ ...f, completion_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Service Vendor</label>
            <input value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} placeholder="(optional — keeps original if blank)" />
          </div>
        </div>

        {/* Physical damage extra fields */}
        {form.service_type === 'Physical Damage' && (
          <div style={{ background:'rgba(245,191,36,0.06)', border:'1px solid rgba(245,191,36,0.2)', borderRadius:'var(--radius)', padding:12, marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'var(--amber)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Physical Damage — Part Details</div>
            <div className="form-row">
              <div className="form-group">
                <label>Replaced Part Name *</label>
                <input value={form.replaced_part_name} onChange={e => setForm(f => ({ ...f, replaced_part_name: e.target.value }))} placeholder="e.g. LCD Panel" />
              </div>
              <div className="form-group">
                <label>Part Serial Number</label>
                <input className="mono" value={form.part_serial_number} onChange={e => setForm(f => ({ ...f, part_serial_number: e.target.value }))} />
              </div>
            </div>
            <div className="form-row" style={{ marginBottom:0 }}>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Part Warranty (Months)</label>
                <input type="number" min={0} value={form.part_warranty_months} onChange={e => setForm(f => ({ ...f, part_warranty_months: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Service Charge (₹)</label>
                <input type="number" min={0} step="0.01" value={form.service_charge} onChange={e => setForm(f => ({ ...f, service_charge: e.target.value }))} />
              </div>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Service Report *</label>
          <textarea rows={3} value={form.service_report} onChange={e => setForm(f => ({ ...f, service_report: e.target.value }))} placeholder="What was done during service…" />
        </div>

        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}
            style={{ background:'var(--green)', borderColor:'var(--green)' }}>
            {busy ? 'Closing…' : '✅ Close Service & Return to Stock'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Service History Modal ─────────────────────────────────────────────────
function ServiceHistoryModal({ asset, onClose }) {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    api.get(`/assets/${asset.id}/service-history`).then(r => setHistory(r.data)).catch(() => setHistory([]));
  }, [asset.id]);

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:600 }}>
        <div className="modal-header">
          <span className="modal-title">Service History — <span className="mono" style={{ color:'var(--accent)' }}>{asset.asset_tag}</span></span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {history === null ? <div style={{ padding:20, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !history.length ? <div className="empty-state"><p>No service records for this asset</p></div>
        : (
          <div style={{ maxHeight:480, overflowY:'auto' }}>
            {history.map((s, i) => (
              <div key={s.id} style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14, marginBottom:12 }}>
                <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:8 }}>
                  <div>
                    <span className="mono" style={{ fontSize:12, color:'var(--accent)', fontWeight:700 }}>{s.ticket_number || `Service #${history.length - i}`}</span>
                    {s.service_type && <span className="badge badge-normal" style={{ marginLeft:8 }}>{s.service_type}</span>}
                  </div>
                  <span className={`badge ${s.status==='Completed'?'badge-completed':'badge-pending'}`}>{s.status}</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 16px', fontSize:12 }}>
                  {[
                    ['Service Date', s.service_date ? new Date(s.service_date).toLocaleDateString() : '—'],
                    ['Return Date',  s.return_date ? new Date(s.return_date).toLocaleDateString() : '—'],
                    ['Vendor',       s.vendor || '—'],
                    ['Warranty at service', s.warranty_status_snapshot ? `${s.warranty_status_snapshot}${s.warranty_remaining_days!=null?` (${fmtRemaining(s.warranty_remaining_days)})`:''}` : '—'],
                    ...(s.service_type === 'Physical Damage' ? [
                      ['Replaced Part', s.replaced_part_name || '—'],
                      ['Part Serial',   s.part_serial_number || '—'],
                      ['Part Warranty', s.part_warranty_months ? `${s.part_warranty_months} months` : '—'],
                      ['Service Charge', s.service_charge != null ? `₹${s.service_charge}` : '—'],
                    ] : []),
                  ].map(([l, v]) => (
                    <div key={l} style={{ display:'flex', justifyContent:'space-between', borderBottom:'1px solid var(--border)', padding:'3px 0' }}>
                      <span style={{ color:'var(--text2)' }}>{l}</span><span style={{ textAlign:'right' }}>{v}</span>
                    </div>
                  ))}
                </div>
                {s.issue_desc && <div style={{ marginTop:8, fontSize:12 }}><span style={{ color:'var(--text2)' }}>Complaint: </span>{s.issue_desc}</div>}
                {s.service_report && <div style={{ marginTop:4, fontSize:12 }}><span style={{ color:'var(--text2)' }}>Report: </span>{s.service_report}</div>}
                <div style={{ marginTop:8, fontSize:11, color:'var(--text2)' }}>
                  Sent by {s.technician_name || '—'}
                  {s.updated_by_name && ` · Closed by ${s.updated_by_name}`}
                  {s.updated_at && ` on ${new Date(s.updated_at).toLocaleDateString()}`}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Bulk Update Modal ─────────────────────────────────────────────────────
function BulkUpdateModal({ count, onSave, onClose, busy }) {
  const [fields, setFields] = useState({ condition:false, location:false, remarks:false });
  const [vals,   setVals]   = useState({ condition:'Good', location:'', remarks:'' });
  const [err,    setErr]    = useState('');
  const toggle = k => setFields(f => ({ ...f, [k]: !f[k] }));
  const handleSave = () => {
    const updates = {};
    if (fields.condition) updates.condition = vals.condition;
    if (fields.location)  updates.location  = vals.location;
    if (fields.remarks)   updates.remarks   = vals.remarks;
    if (!Object.keys(updates).length) { setErr('Select at least one field'); return; }
    onSave(updates);
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:420 }}>
        <div className="modal-header">
          <span className="modal-title">Bulk Update — {count} Asset{count>1?'s':''}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <p style={{ fontSize:13, color:'var(--text2)', marginBottom:14 }}>Tick each field you want to update.</p>
        {[
          { k:'condition', label:'Condition', ctrl:<select disabled={!fields.condition} value={vals.condition} onChange={e=>setVals(v=>({...v,condition:e.target.value}))}>{CONDITIONS.map(c=><option key={c}>{c}</option>)}</select> },
          { k:'location',  label:'Location',  ctrl:<input disabled={!fields.location}  value={vals.location}  onChange={e=>setVals(v=>({...v,location:e.target.value}))}  placeholder="Floor 1, Rack B…" /> },
          { k:'remarks',   label:'Remarks',   ctrl:<input disabled={!fields.remarks}   value={vals.remarks}   onChange={e=>setVals(v=>({...v,remarks:e.target.value}))}   placeholder="After servicing…" /> },
        ].map(row=>(
          <div key={row.k} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <input type="checkbox" checked={fields[row.k]} onChange={()=>toggle(row.k)} style={{ width:16,height:16,flexShrink:0 }} />
            <div className="form-group" style={{ flex:1, marginBottom:0, opacity:fields[row.k]?1:0.4 }}>
              <label>{row.label}</label>{row.ctrl}
            </div>
          </div>
        ))}
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={busy}>{busy?'Updating…':`Update ${count}`}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Assign Modal ──────────────────────────────────────────────────────────
function AssignModal({ asset, campusRooms, campusStaff, onSave, onClose, busy }) {
  const [form, setForm] = useState({
    room_id:  String(asset.room_id  || ''),
    staff_id: String(asset.staff_id || ''),
    location: asset.location || '',
    status:   asset.status || 'active',
  });
  const [err, setErr] = useState('');

  const save = () => {
    if (!form.room_id && !form.staff_id) { setErr('Select a room or a staff person'); return; }
    onSave(form);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:440 }}>
        <div className="modal-header">
          <span className="modal-title">Assign Asset — <span className="mono" style={{ color:'var(--accent)' }}>{asset.asset_tag}</span></span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <p style={{ fontSize:13, color:'var(--text2)', marginBottom:14 }}>
          <strong>{asset.brand} {asset.model}</strong> · {asset.asset_type}
        </p>

        <div style={{ background:'var(--bg3)', borderRadius:'var(--radius)', padding:12, marginBottom:14, fontSize:12, color:'var(--text2)' }}>
          Assign to a <strong style={{ color:'var(--text)' }}>Room</strong> (shared / classroom asset) <strong style={{ color:'var(--text)' }}>or</strong> a <strong style={{ color:'var(--text)' }}>Staff Person</strong> (personal asset). Selecting one clears the other.
        </div>

        <div className="form-group">
          <label>Assign to Room <span style={{ fontSize:11, color:'var(--text2)' }}>(shared asset)</span></label>
          <select value={form.room_id} onChange={e => setForm(f => ({ ...f, room_id: e.target.value, staff_id: e.target.value ? '' : f.staff_id }))}>
            <option value="">— Not a room asset —</option>
            {campusRooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>

        <div style={{ textAlign:'center', color:'var(--text2)', fontSize:12, margin:'4px 0' }}>— or —</div>

        <div className="form-group">
          <label>Assign to Staff <span style={{ fontSize:11, color:'var(--text2)' }}>(personal asset)</span></label>
          <select value={form.staff_id} onChange={e => setForm(f => ({ ...f, staff_id: e.target.value, room_id: e.target.value ? '' : f.room_id }))}>
            <option value="">— Not assigned to a person —</option>
            {campusStaff.map(s => <option key={s.id} value={s.id}>{s.name}{s.designation ? ` (${s.designation})` : ''}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>Sub-location <span style={{ fontSize:11, color:'var(--text2)' }}>(shelf, desk, rack…)</span></label>
          <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Rack 2, Desk 3B" />
        </div>

        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Assigning…' : 'Assign Asset'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── IT Asset Register ───────────────────────────────────────────────────────
function AssetRegister() {
  const { user, isAdmin, can } = useAuth();
  const [assets,      setAssets]      = useState([]);
  const [campuses,    setCampuses]    = useState([]);
  const [brands,      setBrands]      = useState([]);
  const [models,      setModels]      = useState([]);
  const [assetTypes,  setAssetTypes]  = useState([]);   // from Device Types master
  const [campusRooms, setCampusRooms] = useState([]);
  const [campusStaff, setCampusStaff] = useState([]);
  const [filters,     setFilters]     = useState({ search:'', condition:'', status:'', campus_id:'' });
  const [modal,       setModal]       = useState(false);   // add/edit modal
  const [scanning,    setScanning]    = useState(false);   // barcode camera open
  const [assignModal, setAssignModal] = useState(null);    // asset to assign
  const [sendModal,   setSendModal]   = useState(null);    // asset to send for service
  const [updateModal, setUpdateModal] = useState(null);    // asset to update service
  const [historyModal,setHistoryModal]= useState(null);    // asset service history
  const [editing,     setEditing]     = useState(null);
  const [form,        setForm]        = useState({});
  const [busy,        setBusy]        = useState(false);
  const [err,         setErr]         = useState('');
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(new Set());
  const [bulkModal,   setBulkModal]   = useState(false);
  const [uploadModal, setUploadModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search)    params.search    = filters.search;
      if (filters.condition) params.condition = filters.condition;
      if (filters.status)    params.status    = filters.status;
      if (filters.campus_id) params.campus_id = filters.campus_id;
      const r = await api.get('/assets', { params });
      setAssets(r.data);
    } catch(e) {}
    setLoading(false);
    setSelected(new Set());
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data));
    api.get('/master/brands').then(r => setBrands(r.data.filter(b => b.active)));
    api.get('/master/device-types').then(r => setAssetTypes(r.data.filter(t => t.active).map(t => t.name).sort()));
  }, []);

  useEffect(() => {
    if (form.brand_id && form.brand_id !== 'other') {
      api.get(`/master/models/${form.brand_id}`).then(r => setModels(r.data.filter(m => m.active)));
    } else setModels([]);
  }, [form.brand_id]);

  // Load rooms+staff for assign modal
  const loadCampusData = async campusId => {
    if (!campusId) return;
    const [r, s] = await Promise.all([
      api.get(`/master/rooms/${campusId}`),
      api.get(`/master/informedby/${campusId}`)
    ]);
    setCampusRooms(r.data.filter(x => x.active));
    setCampusStaff(s.data.filter(x => x.active));
  };

  const openAdd = () => {
    const defaultCampus = isAdmin ? '' : String(user.campus_ids?.[0] || '');
    setForm({ campus_id: defaultCampus, brand_id:'', brand_custom:'', model_id:'', model_custom:'',
      asset_type:'', serial_number:'', mac_address:'', specifications:'', department:'', location:'',
      purchase_date:'', warranty_period_months:'', warranty_unit:'months', warranty_expiry:'',
      condition:'Good', remarks:'' });
    setEditing(null); setErr(''); setModal(true);
  };

  const delAsset = async a => {
    if (!window.confirm(`Delete asset ${a.asset_tag}?\n\n"${a.brand||''} ${a.model||''}"\n\nIt is soft-deleted — hidden everywhere but recoverable from the audit trail.`)) return;
    try { await api.delete(`/assets/${a.id}`); load(); }
    catch (e) { window.alert(e.response?.data?.error || 'Delete failed'); }
  };
  const openEdit = a => {
    const mb = brands.find(b => b.name?.toLowerCase() === a.brand?.toLowerCase());
    setForm({
      campus_id: String(a.campus_id || ''), room_id: String(a.room_id || ''),
      staff_id: String(a.staff_id || ''),
      brand_id: mb ? String(mb.id) : (a.brand ? 'other' : ''),
      brand_custom: mb ? '' : (a.brand || ''), model_id:'', model_custom: a.model||'',
      asset_type: a.asset_type||'', serial_number: a.serial_number||'', mac_address: a.mac_address||'',
      specifications: a.specifications||'', department: a.department||'',
      location: a.location||'', status: a.status||'active',
      purchase_date: a.purchase_date ? a.purchase_date.slice(0,10) : '',
      warranty_period_months: a.warranty_period_months || '',
      warranty_unit: 'months',
      warranty_expiry: a.warranty_expiry ? a.warranty_expiry.slice(0,10) : '',
      condition: a.condition||'Good', remarks: a.remarks||''
    });
    setEditing(a); setErr(''); setModal(true);
  };

  const openAssign = a => { loadCampusData(a.campus_id); setAssignModal(a); };

  useEffect(() => {
    if (editing && models.length && form.model_custom) {
      const match = models.find(m => m.name?.toLowerCase() === form.model_custom?.toLowerCase());
      if (match) setForm(f => ({ ...f, model_id: String(match.id), model_custom:'' }));
    }
  // eslint-disable-next-line
  }, [models]);

  const getBrand = () => form.brand_id === 'other' ? form.brand_custom.trim() : (brands.find(b=>String(b.id)===form.brand_id)?.name||form.brand_custom.trim());
  const getModel = () => (!form.model_id||form.model_id==='other') ? form.model_custom.trim() : (models.find(m=>String(m.id)===form.model_id)?.name||form.model_custom.trim());

  const save = async () => {
    const brand = getBrand();
    if (!form.campus_id)             { setErr('Campus is required'); return; }
    if (!brand)                      { setErr('Brand is required'); return; }
    if (!form.specifications.trim()) { setErr('Specifications is required'); return; }
    setBusy(true); setErr('');
    try {
      const payload = {
        campus_id:      parseInt(form.campus_id),
        // On ADD: no room/staff — goes to stock. On EDIT: keep existing room/staff unless editing changes them.
        room_id:        editing ? (form.room_id ? parseInt(form.room_id) : null) : null,
        staff_id:       editing ? (form.staff_id ? parseInt(form.staff_id) : null) : null,
        brand, model: getModel(),
        asset_type:     form.asset_type||null, serial_number: form.serial_number||null, mac_address: form.mac_address||null,
        specifications: form.specifications.trim(),
        department:     form.department||null,
        location:       form.location||null,
        status:         editing ? (form.status||undefined) : 'stock',  // add always → stock
        purchase_date:  form.purchase_date||null,
        warranty_period_months: form.warranty_period_months ? parseInt(form.warranty_period_months) : null,
        warranty_expiry: computeExpiry(form.purchase_date, form.warranty_period_months) || form.warranty_expiry || null,
        condition:      form.condition, remarks: form.remarks||null,
      };
      if (editing) await api.put(`/assets/${editing.id}`, payload);
      else         await api.post('/assets', payload);
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error||'Save failed'); }
    finally { setBusy(false); }
  };

  // Carry over all asset fields so a PUT never wipes data
  const assetBase = a => ({
    brand: a.brand, model: a.model, asset_type: a.asset_type, serial_number: a.serial_number,
    specifications: a.specifications, department: a.department, condition: a.condition,
    remarks: a.remarks, purchase_date: a.purchase_date || null,
    warranty_period_months: a.warranty_period_months || null,
    warranty_expiry: a.warranty_expiry || null,
  });

  const doAssign = async assignForm => {
    setBusy(true);
    try {
      await api.put(`/assets/${assignModal.id}`, {
        ...assetBase(assignModal),
        room_id:  assignForm.room_id  ? parseInt(assignForm.room_id)  : null,
        staff_id: assignForm.staff_id ? parseInt(assignForm.staff_id) : null,
        location: assignForm.location || null,
        status: 'active',
      });
      setAssignModal(null); load();
    } catch(e) { setErr(e.response?.data?.error||'Assign failed'); }
    finally { setBusy(false); }
  };

  const doUnassign = async a => {
    if (!window.confirm(`Unassign ${a.asset_tag} and return to stock?`)) return;
    await api.put(`/assets/${a.id}`, {
      ...assetBase(a), room_id: null, staff_id: null, status: 'stock',
    });
    load();
  };

  const doSendService = async svcForm => {
    setBusy(true);
    try {
      await api.post(`/assets/${sendModal.id}/send-service`, svcForm);
      setSendModal(null); load();
    } catch(e) { setErr(e.response?.data?.error||'Failed to send for service'); }
    finally { setBusy(false); }
  };

  const doCompleteService = async svcForm => {
    setBusy(true);
    try {
      await api.post(`/assets/${updateModal.id}/complete-service`, svcForm);
      setUpdateModal(null); load();
    } catch(e) { setErr(e.response?.data?.error||'Failed to complete service'); }
    finally { setBusy(false); }
  };

  const allIds = assets.map(a => a.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const toggleOne = id => { const s = new Set(selected); s.has(id)?s.delete(id):s.add(id); setSelected(s); };
  const doBulkUpdate = async updates => {
    setBusy(true);
    try { await api.post('/assets/bulk-update', { ids:[...selected], updates }); setBulkModal(false); load(); }
    catch(e) {} finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:20 }}>
        <h2 style={{ fontSize:18, fontWeight:600 }}>Asset Register</h2>
        <div className="flex-gap" style={{ gap:8 }}>
          {selected.size > 0 && <button className="btn btn-secondary" onClick={() => setBulkModal(true)}>✎ Update {selected.size}</button>}
          {isAdmin && <button className="btn btn-secondary" onClick={() => setUploadModal(true)}>⬆ Bulk Upload</button>}
          {can('assets','create') && <button className="btn btn-primary" onClick={openAdd}>+ Add Asset</button>}
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom:16, padding:12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:10 }}>
          <input placeholder="Search by tag, brand, model, serial, room, staff…"
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          <select value={filters.condition} onChange={e => setFilters(f => ({ ...f, condition: e.target.value }))}>
            <option value="">All Conditions</option>
            {CONDITIONS.map(c => <option key={c}>{c}</option>)}
          </select>
          {isAdmin && (
            <select value={filters.campus_id} onChange={e => setFilters(f => ({ ...f, campus_id: e.target.value }))}>
              <option value="">All Campuses</option>
              {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !assets.length ? <div className="empty-state"><h3>No assets found</h3></div>
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width:36 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ width:14,height:14 }} /></th>
                  <th>Asset Tag</th><th>Brand / Model</th><th>Type</th><th>Specs</th>
                  <th>Assigned To</th><th>Campus</th><th>Status</th><th>Condition</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {assets.map(a => (
                  <tr key={a.id} style={{ background: selected.has(a.id) ? 'rgba(79,142,247,0.06)' : '' }}>
                    <td><input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleOne(a.id)} style={{ width:14,height:14 }} /></td>
                    <td><span className="mono" style={{ fontSize:12, color:'var(--accent)' }}>{a.asset_tag}</span></td>
                    <td><div style={{ fontWeight:500 }}>{a.brand}</div><div style={{ fontSize:12, color:'var(--text2)' }}>{a.model||'—'}</div></td>
                    <td><span style={{ fontSize:12 }}>{a.asset_type||'—'}</span></td>
                    <td><span style={{ fontSize:11, color:'var(--text2)' }}>{a.specifications ? (a.specifications.length>40?a.specifications.slice(0,40)+'…':a.specifications) : '—'}</span></td>
                    <td>
                      {a.room_name  && <div style={{ fontSize:12 }}>📍 {a.room_name}</div>}
                      {a.staff_name && <div style={{ fontSize:12 }}>👤 {a.staff_name}</div>}
                      {!a.room_name && !a.staff_name && <span style={{ fontSize:12, color:'var(--text2)' }}>—</span>}
                    </td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{a.campus_name}</span></td>
                    <td><span className={`badge ${STATUS_COLORS[a.status]||''}`}>{STATUS_LABELS[a.status]||a.status}</span></td>
                    <td>{condBadge(a.condition)}</td>
                    <td>
                      <div className="flex-gap" style={{ gap:4, flexWrap:'wrap' }}>
                        {can('assets','edit') && <button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}>Edit</button>}
                        {['admin','it_head'].includes(user?.role) && <button className="btn btn-danger btn-sm" onClick={() => delAsset(a)}>Delete</button>}
                        {can('assets','edit') && a.status === 'stock' && (
                          <button className="btn btn-primary btn-sm" onClick={() => openAssign(a)}>Assign</button>
                        )}
                        {can('assets','edit') && a.status === 'active' && (a.room_id || a.staff_id) && (
                          <button className="btn btn-secondary btn-sm" onClick={() => doUnassign(a)} title="Return to stock">↩ Unassign</button>
                        )}
                        {can('assets','edit') && (a.status === 'stock' || a.status === 'active') && (
                          <button className="btn btn-secondary btn-sm" onClick={() => setSendModal(a)} title="Send for service">🔧 Service</button>
                        )}
                        {can('assets','edit') && a.status === 'sent_for_service' && (
                          <button className="btn btn-primary btn-sm" style={{ background:'var(--amber)', borderColor:'var(--amber)', color:'#1a1a1a' }} onClick={() => setUpdateModal(a)}>Update Service</button>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={() => setHistoryModal(a)} title="Service history">📋</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:620 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? `Edit — ${editing.asset_tag}` : 'Add New Asset to Stock'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>

            {!editing && (
              <div style={{ background:'rgba(79,142,247,0.08)', border:'1px solid rgba(79,142,247,0.2)', borderRadius:'var(--radius)', padding:'8px 12px', marginBottom:14, fontSize:12, color:'var(--text2)' }}>
                📦 New assets are added to <strong style={{ color:'var(--accent)' }}>Stock</strong>. Use the <strong>Assign</strong> button from the table to assign to a room or staff.
              </div>
            )}

            <div className="form-group">
              <label>Campus *</label>
              <select value={form.campus_id}
                onChange={e => setForm(f => ({ ...f, campus_id: e.target.value }))}
                disabled={!isAdmin && user?.campus_ids?.length === 1}>
                <option value="">— Select —</option>
                {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div style={{ height:1, background:'var(--border)', margin:'4px 0 12px' }} />
            <div style={{ fontSize:11, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Asset Details</div>

            <div className="form-row">
              <div className="form-group">
                <label>Brand *</label>
                <select value={form.brand_id} onChange={e => setForm(f => ({ ...f, brand_id: e.target.value, model_id:'', model_custom:'' }))}>
                  <option value="">— Select brand —</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  <option value="other">Other (type below)</option>
                </select>
                {form.brand_id === 'other' && (
                  <input style={{ marginTop:6 }} value={form.brand_custom} onChange={e => setForm(f => ({ ...f, brand_custom: e.target.value }))} placeholder="Brand name…" />
                )}
              </div>
              <div className="form-group">
                <label>Model</label>
                {form.brand_id && form.brand_id !== 'other' && models.length > 0 ? (
                  <>
                    <select value={form.model_id} onChange={e => setForm(f => ({ ...f, model_id: e.target.value, model_custom:'' }))}>
                      <option value="">— Select model —</option>
                      {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      <option value="other">Other…</option>
                    </select>
                    {form.model_id === 'other' && (
                      <input style={{ marginTop:6 }} value={form.model_custom} onChange={e => setForm(f => ({ ...f, model_custom: e.target.value }))} />
                    )}
                  </>
                ) : (
                  <input value={form.model_custom} onChange={e => setForm(f => ({ ...f, model_custom: e.target.value }))}
                    placeholder={form.brand_id === 'other' ? 'Model…' : 'Select brand first'} />
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Asset Type</label>
                <select value={form.asset_type} onChange={e => setForm(f => ({ ...f, asset_type: e.target.value }))}>
                  <option value="">— Select —</option>
                  {(assetTypes.length ? assetTypes : TYPES).map(t => <option key={t}>{t}</option>)}
                  {/* keep current value selectable even if not in master */}
                  {form.asset_type && !(assetTypes.length ? assetTypes : TYPES).includes(form.asset_type) && (
                    <option value={form.asset_type}>{form.asset_type}</option>
                  )}
                </select>
              </div>
              <div className="form-group">
                <label>Serial Number</label>
                {/* Serials are printed as barcodes, so on a phone they can be
                    scanned instead of typed — far fewer mistakes than reading
                    a 20-character code off a sticker. */}
                <div className="flex-gap" style={{ gap:6, alignItems:'center' }}>
                  <input className="mono" style={{ flex:1 }} value={form.serial_number}
                    onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} />
                  {barcodeSupported() && (
                    <button type="button" className="btn btn-secondary btn-sm" title="Scan the barcode"
                      onClick={() => setScanning(true)}>▤ Scan</button>
                  )}
                </div>
              </div>
              {/* Only the networked machines have one, so the field appears
                  for those types and stays out of the way for the rest. */}
              {MAC_TYPES.includes(String(form.asset_type || '').toUpperCase()) && (
                <div className="form-group">
                  <label>MAC Address</label>
                  <input className="mono" value={form.mac_address}
                    placeholder="00:1A:2B:3C:4D:5E"
                    onChange={e => setForm(f => ({ ...f, mac_address: e.target.value }))} />
                  <span style={{ fontSize:11, color:'var(--text2)' }}>
                    Any format — stored as 00:1A:2B:3C:4D:5E so a search matches.
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Specifications *</label>
              <input value={form.specifications} onChange={e => setForm(f => ({ ...f, specifications: e.target.value }))}
                placeholder="e.g. Intel i7, 16GB RAM, 512GB SSD" />
            </div>

            <div style={{ height:1, background:'var(--border)', margin:'4px 0 12px' }} />
            <div style={{ fontSize:11, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Purchase & Status</div>

            <div className="form-row">
              <div className="form-group">
                <label>Condition</label>
                <select value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}>
                  {CONDITIONS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Department</label>
                <input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} />
              </div>
            </div>

            {/* Warranty */}
            <div className="form-row-3">
              <div className="form-group">
                <label>Purchase Date</label>
                <input type="date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Warranty Period (Months) <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span></label>
                <input type="number" min={0} value={form.warranty_period_months || ''}
                  onChange={e => setForm(f => ({ ...f, warranty_period_months: e.target.value, warranty_expiry:'' }))}
                  placeholder="e.g. 36 (= 3 years)" />
              </div>
              <div className="form-group">
                <label>Warranty Expiry <span style={{ fontSize:11, color:'var(--text2)' }}>(auto)</span></label>
                <input type="date"
                  value={computeExpiry(form.purchase_date, form.warranty_period_months) || form.warranty_expiry || ''}
                  onChange={e => setForm(f => ({ ...f, warranty_expiry: e.target.value, warranty_period_months:'' }))} />
              </div>
            </div>

            {/* Warranty status display */}
            {(() => {
              const exp = computeExpiry(form.purchase_date, form.warranty_period_months) || form.warranty_expiry;
              if (!exp) return null;
              const wt = warrantyInfo(exp);
              return (
                <div style={{ display:'flex', gap:20, padding:'8px 12px', background:'var(--bg3)', borderRadius:'var(--radius)', marginBottom:14, fontSize:12 }}>
                  <span><span style={{ color:'var(--text2)' }}>Status: </span><span style={{ color:wt.color, fontWeight:600 }}>{wt.status}</span></span>
                  <span><span style={{ color:'var(--text2)' }}>Remaining: </span><span style={{ fontWeight:500 }}>{wt.remaining}</span></span>
                </div>
              );
            })()}

            <div className="form-group">
              <label>Remarks</label>
              <input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
            </div>

            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>
                {busy ? 'Saving…' : editing ? 'Save Changes' : 'Add to Stock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode camera — fills in the serial number and closes itself. */}
      {scanning && (
        <BarcodeScanner
          onFound={code => { setForm(f => ({ ...f, serial_number: code })); setScanning(false); }}
          onClose={() => setScanning(false)} />
      )}

      {/* Assign Modal */}
      {assignModal && (
        <AssignModal
          asset={assignModal}
          campusRooms={campusRooms}
          campusStaff={campusStaff}
          onSave={doAssign}
          onClose={() => setAssignModal(null)}
          busy={busy}
        />
      )}

      {/* Send for Service Modal */}
      {sendModal && (
        <SendServiceModal asset={sendModal} onSave={doSendService} onClose={() => setSendModal(null)} busy={busy} />
      )}

      {/* Update Service Modal */}
      {updateModal && (
        <UpdateServiceModal asset={updateModal} onSave={doCompleteService} onClose={() => setUpdateModal(null)} busy={busy} />
      )}

      {/* Service History Modal */}
      {historyModal && (
        <ServiceHistoryModal asset={historyModal} onClose={() => setHistoryModal(null)} />
      )}

      {/* Bulk Update Modal */}
      {bulkModal && (
        <BulkUpdateModal count={selected.size} onSave={doBulkUpdate} onClose={() => setBulkModal(false)} busy={busy} />
      )}

      {/* Bulk Upload Modal */}
      {uploadModal && (
        <BulkUploadModal onClose={() => setUploadModal(false)} onDone={load} />
      )}
    </div>
  );
}

// ─── Student Laptops Tab ─────────────────────────────────────────────────────
// Target fields the importer needs, with auto-guess synonyms (normalized headers)
const SL_FIELDS = [
  { key:'campus',           label:'Campus',                req:true,  syn:['campus','campuscode','campusname','branch'] },
  { key:'student_id_number',label:'Student ID Number',     req:false, syn:['idnumber','studentid','studentidnumber','admissionno','admissionnumber','rollno','rollnumber','regno'] },
  { key:'student_name',     label:'Student Name',          req:false, syn:['studentname','name','nameofstudent','student'] },
  { key:'brand',            label:'Brand',                 req:false, syn:['brand','laptopbrand','make','manufacturer','devicebrand','laptopmake'] },
  { key:'model',            label:'Model',                 req:false, syn:['model','laptopmodel','modelname','modelno','modelnumber','devicemodel'] },
  { key:'serial_number',    label:'Serial Number',         req:false, syn:['serialnumber','serial','serialno','laptopserial','laptopserialnumber','snumber','sno','assetserial'] },
  { key:'os_license',       label:'OS License (Yes/No)',   req:false, syn:['oslicense','licensed','license','oslicensed','windowslicense','os'] },
  { key:'charger_serial',   label:'Charger Serial Number', req:false, syn:['chargerserialnumber','chargerserial','charger','chargerno','adapterserial'] },
  { key:'distributed_date', label:'Distributed Date',      req:false, syn:['distributeddate','distributiondate','dateofdistribution','date','issueddate','handoverdate'] },
];

function StudentLaptopCSVModal({ onClose, onDone }) {
  const [step, setStep]     = useState('pick');   // pick → map → result
  const [rawRows, setRawRows] = useState([]);     // objects keyed by original header
  const [headers, setHeaders] = useState([]);     // original column headers
  const [mapping, setMapping] = useState({});     // target key → original header
  const [result, setResult] = useState(null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');

  const norm = h => String(h||'').toLowerCase().replace(/[^a-z0-9]/g,'');

  const handleFile = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const wb = XLSX.read(evt.target.result, { type:'array' });
        let best = [];
        wb.SheetNames.forEach(sn => { const a = XLSX.utils.sheet_to_json(wb.Sheets[sn], { defval:'' }); if (a.length > best.length) best = a; });
        if (!best.length) { setErr('No rows found in the file.'); return; }
        const hdrs = Object.keys(best[0]);
        // Auto-guess mapping
        const guess = {};
        SL_FIELDS.forEach(f => {
          const found = hdrs.find(h => f.syn.includes(norm(h)));
          if (found) guess[f.key] = found;
        });
        setRawRows(best); setHeaders(hdrs); setMapping(guess); setErr(''); setStep('map');
      } catch(ex) { setErr('Could not read file: ' + ex.message); }
    };
    reader.readAsArrayBuffer(file);
  };

  const buildRecords = () => rawRows.map(raw => {
    const rec = {};
    SL_FIELDS.forEach(f => { rec[f.key] = mapping[f.key] ? String(raw[mapping[f.key]] ?? '').trim() : ''; });
    return rec;
  }).filter(r => r.campus || r.serial_number);

  const doUpload = async () => {
    if (!mapping.campus) { setErr('Please map the Campus column — it is required.'); return; }
    const records = buildRecords();
    if (!records.length) { setErr('No usable rows after mapping.'); return; }
    setBusy(true); setErr('');
    try {
      const CHUNK = 1000; let agg = { inserted:0, skipped:0, errors:[] };
      for (let i=0;i<records.length;i+=CHUNK) {
        const d = (await api.post('/student-laptops/import', { records: records.slice(i,i+CHUNK) })).data;
        agg.inserted += d.inserted; agg.skipped += d.skipped; agg.errors = agg.errors.concat(d.errors||[]);
      }
      setResult(agg); setStep('result');
    } catch(ex) { setErr(ex.response?.data?.error || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const downloadSample = () => {
    const csv = 'Campus,ID Number,Student Name,Brand,Model,Serial Number,OS License,Charger Serial Number,Distributed Date\nCBE-MAIN,STU2024001,Arjun S,Dell,Latitude 3420,DLSER123,Yes,CHG123,2025-06-01\n';
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download='student_laptops_sample.csv'; a.click();
  };

  const sample = buildRecords();

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:680 }}>
        <div className="modal-header">
          <span className="modal-title">Bulk Upload Student Laptops</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {step === 'pick' && (<>
          <div style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:16, fontSize:13, lineHeight:1.7 }}>
            <strong>Campus</strong> is required. After you choose a file, you'll map your columns to the fields — so any header names work.<br/>
            <span style={{ color:'var(--text2)' }}>Student ID/Name link to a student in that campus (if it exists). Duplicate serials are skipped.</span>
            <div><button className="btn btn-secondary btn-sm" style={{ marginTop:10 }} onClick={downloadSample}>⬇ Download Sample CSV</button></div>
          </div>
          <div className="form-group">
            <label>Select File (.xlsx / .csv)</label>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ padding:'8px 0', background:'transparent', border:'none', color:'var(--text)' }} />
          </div>
          {err && <div className="err-msg">{err}</div>}
          <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button></div>
        </>)}

        {step === 'map' && (<>
          <div style={{ fontSize:13, color:'var(--text2)', marginBottom:12 }}>
            <strong style={{ color:'var(--text)' }}>{rawRows.length}</strong> rows found. Match your file's columns to each field
            (auto-detected where possible). Unmapped fields are left blank.
          </div>
          <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden', marginBottom:14 }}>
            {SL_FIELDS.map((f,i) => (
              <div key={f.key} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, alignItems:'center', padding:'8px 12px', borderTop: i ? '1px solid var(--border)' : 'none', background: i%2 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                <div style={{ fontSize:13 }}>{f.label}{f.req && <span style={{ color:'var(--red)' }}> *</span>}
                  {mapping[f.key] && <span style={{ color:'var(--green)', fontSize:11, marginLeft:6 }}>✓ matched</span>}
                </div>
                <select value={mapping[f.key]||''} onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value }))}
                  style={{ fontSize:12, borderColor: f.req && !mapping[f.key] ? 'var(--red)' : undefined }}>
                  <option value="">— Not in my file —</option>
                  {headers.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>

          {/* Live preview of mapped data */}
          <div style={{ fontSize:11, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', marginBottom:6 }}>Preview ({sample.length} rows)</div>
          <div className="table-wrap" style={{ maxHeight:200, overflowY:'auto', marginBottom:14 }}>
            <table><thead><tr><th>Campus</th><th>Student</th><th>Brand/Model</th><th>Serial</th><th>OS</th><th>Charger</th><th>Date</th></tr></thead>
              <tbody>{sample.slice(0,20).map((r,i)=>(<tr key={i}>
                <td style={{ fontSize:11 }}>{r.campus||'—'}</td>
                <td style={{ fontSize:11 }}>{r.student_id_number||r.student_name||'—'}</td>
                <td style={{ fontSize:11 }}>{[r.brand,r.model].filter(Boolean).join(' ')||'—'}</td>
                <td className="mono" style={{ fontSize:11 }}>{r.serial_number||'—'}</td>
                <td style={{ fontSize:11 }}>{r.os_license||'—'}</td>
                <td className="mono" style={{ fontSize:11 }}>{r.charger_serial||'—'}</td>
                <td style={{ fontSize:11 }}>{r.distributed_date||'—'}</td>
              </tr>))}</tbody></table>
          </div>
          {err && <div className="err-msg">{err}</div>}
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={()=>setStep('pick')}>← Back</button>
            <button className="btn btn-primary" onClick={doUpload} disabled={busy}>{busy?'Importing…':`Import ${sample.length}`}</button>
          </div>
        </>)}

        {step === 'result' && result && (<>
          <div style={{ display:'flex', gap:12, marginBottom:16 }}>
            <div style={{ flex:1, padding:14, background:'rgba(52,211,153,0.1)', borderRadius:10, textAlign:'center' }}><div style={{ fontSize:24, fontWeight:700, color:'var(--green)' }}>{result.inserted}</div><div style={{ fontSize:11, color:'var(--text2)' }}>Inserted</div></div>
            <div style={{ flex:1, padding:14, background:'rgba(245,191,36,0.1)', borderRadius:10, textAlign:'center' }}><div style={{ fontSize:24, fontWeight:700, color:'var(--amber)' }}>{result.skipped}</div><div style={{ fontSize:11, color:'var(--text2)' }}>Skipped</div></div>
          </div>
          {result.errors?.length>0 && <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--red)', maxHeight:160, overflowY:'auto' }}>{result.errors.slice(0,30).map((e,i)=><div key={i}>• {e}</div>)}</div>}
          <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setStep('pick')}>Upload Another</button><button className="btn btn-primary" onClick={()=>{onDone();onClose();}}>Done</button></div>
        </>)}
      </div>
    </div>
  );
}

function StudentLaptops() {
  const { user, isAdmin } = useAuth();
  const [laptops,  setLaptops]  = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [students, setStudents] = useState([]);
  const [filters,  setFilters]  = useState({ search:'', campus_id:'' });
  const [modal,    setModal]    = useState(false);
  const [csvModal, setCsvModal] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [stuSearch,setStuSearch]= useState('');
  const empty = { campus_id:'', student_id:'', brand:'', model:'', serial_number:'', os_license:false, charger_serial:'', distributed_date:'' };
  const [form,     setForm]     = useState(empty);
  const [busy,     setBusy]     = useState(false);
  const [err,      setErr]      = useState('');
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search)    params.search    = filters.search;
      if (filters.campus_id) params.campus_id = filters.campus_id;
      const r = await api.get('/student-laptops', { params });
      setLaptops(r.data);
    } catch(e) {}
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/master/campuses').then(r => setCampuses(r.data)); }, []);

  // Load students for the form's campus
  useEffect(() => {
    if (form.campus_id) api.get(`/master/students/${form.campus_id}`).then(r => setStudents(r.data.filter(s => s.active)));
    else setStudents([]);
  }, [form.campus_id]);

  const openNew = () => {
    const dc = isAdmin ? '' : String(user.campus_ids?.[0] || '');
    setForm({ ...empty, campus_id: dc }); setEditing(null); setStuSearch(''); setErr(''); setModal(true);
  };
  const openEdit = l => {
    setForm({ campus_id:String(l.campus_id||''), student_id:String(l.student_id||''), brand:l.brand||'', model:l.model||'',
      serial_number:l.serial_number||'', os_license:!!l.os_license, charger_serial:l.charger_serial||'',
      distributed_date: l.distributed_date ? l.distributed_date.slice(0,10) : '', active:l.active });
    setEditing(l); setStuSearch(l.student_name||''); setErr(''); setModal(true);
  };

  const save = async () => {
    if (!form.campus_id) { setErr('Campus is required'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { ...form, campus_id:parseInt(form.campus_id), student_id: form.student_id?parseInt(form.student_id):null,
        distributed_date: form.distributed_date||null };
      if (editing) await api.put(`/student-laptops/${editing.id}`, payload);
      else         await api.post('/student-laptops', payload);
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const filteredStudents = students.filter(s => !stuSearch || s.name.toLowerCase().includes(stuSearch.toLowerCase()) || (s.id_number||'').toLowerCase().includes(stuSearch.toLowerCase()));

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:16 }}>
        <div style={{ fontSize:13, color:'var(--text2)' }}>Laptops distributed to students — separate from the IT asset register.</div>
        <div className="flex-gap" style={{ gap:8 }}>
          {isAdmin && <button className="btn btn-secondary" onClick={() => setCsvModal(true)}>⬆ Bulk Upload</button>}
          {isAdmin && <button className="btn btn-primary" onClick={openNew}>+ Add Laptop</button>}
        </div>
      </div>

      <div className="card" style={{ marginBottom:16, padding:12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10 }}>
          <input placeholder="Search by serial, brand, model, charger, student…" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
          {isAdmin && (
            <select value={filters.campus_id} onChange={e => setFilters(f => ({ ...f, campus_id: e.target.value }))}>
              <option value="">All Campuses</option>
              {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !laptops.length ? <div className="empty-state"><h3>No student laptops</h3><p>Add one or bulk upload.</p></div>
        : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Student</th><th>Brand / Model</th><th>Serial Number</th><th>OS License</th><th>Charger Serial</th><th>Distributed</th><th>Campus</th><th>Action</th></tr></thead>
              <tbody>
                {laptops.map(l => (
                  <tr key={l.id} style={{ opacity: l.active ? 1 : 0.55 }}>
                    <td>
                      <div style={{ fontWeight:500 }}>{l.student_name || '—'}</div>
                      {(l.student_grade || l.student_id_number) && <div style={{ fontSize:11, color:'var(--text2)' }}>{l.student_id_number}{l.student_grade ? ` · Grade ${l.student_grade}${l.student_section?`-${l.student_section}`:''}` : ''}</div>}
                    </td>
                    <td><div style={{ fontWeight:500 }}>{l.brand||'—'}</div><div style={{ fontSize:12, color:'var(--text2)' }}>{l.model||'—'}</div></td>
                    <td><span className="mono" style={{ fontSize:12 }}>{l.serial_number||'—'}</span></td>
                    <td><span className={`badge ${l.os_license ? 'badge-good' : 'badge-faulty'}`}>{l.os_license ? 'Yes' : 'No'}</span></td>
                    <td><span className="mono" style={{ fontSize:12 }}>{l.charger_serial||'—'}</span></td>
                    <td><span style={{ fontSize:12 }}>{l.distributed_date ? new Date(l.distributed_date).toLocaleDateString() : '—'}</span></td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{l.campus_name}</span></td>
                    <td>{isAdmin && <button className="btn btn-secondary btn-sm" onClick={() => openEdit(l)}>Edit</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:520 }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Student Laptop' : 'Add Student Laptop'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Campus *</label>
                <select value={form.campus_id} disabled={!!editing || (!isAdmin && user?.campus_ids?.length===1)}
                  onChange={e => setForm(f => ({ ...f, campus_id: e.target.value, student_id:'' }))}>
                  <option value="">— Select —</option>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ position:'relative' }}>
                <label>Student <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span></label>
                <input value={stuSearch} disabled={!form.campus_id}
                  onChange={e => { setStuSearch(e.target.value); setForm(f => ({ ...f, student_id:'' })); }}
                  placeholder="Search name or ID…" autoComplete="off" />
                {stuSearch && !form.student_id && filteredStudents.length > 0 && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:99, background:'var(--bg2)', border:'1px solid var(--accent)', borderRadius:'0 0 var(--radius) var(--radius)', maxHeight:160, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                    {filteredStudents.slice(0,30).map(s => (
                      <div key={s.id} onClick={() => { setStuSearch(s.name); setForm(f => ({ ...f, student_id: String(s.id) })); }}
                        style={{ padding:'7px 12px', cursor:'pointer', fontSize:13, borderBottom:'1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background='var(--bg3)'} onMouseLeave={e => e.currentTarget.style.background=''}>
                        <div style={{ fontWeight:500 }}>{s.name}</div>
                        <div style={{ fontSize:11, color:'var(--text2)' }}>{s.id_number}{s.grade?` · Grade ${s.grade}${s.section?`-${s.section}`:''}`:''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Laptop Brand</label><input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="e.g. Dell" /></div>
              <div className="form-group"><label>Model</label><input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder="e.g. Latitude 3420" /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Serial Number</label><input className="mono" value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} /></div>
              <div className="form-group"><label>Charger Serial Number</label><input className="mono" value={form.charger_serial} onChange={e => setForm(f => ({ ...f, charger_serial: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>OS License</label>
                <select value={form.os_license ? 'yes' : 'no'} onChange={e => setForm(f => ({ ...f, os_license: e.target.value === 'yes' }))}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
              <div className="form-group"><label>Distributed Date</label><input type="date" value={form.distributed_date} onChange={e => setForm(f => ({ ...f, distributed_date: e.target.value }))} /></div>
            </div>
            {editing && (
              <div className="form-group"><label>Status</label>
                <select value={form.active ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'active' }))}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            )}
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Add Laptop'}</button>
            </div>
          </div>
        </div>
      )}

      {csvModal && <StudentLaptopCSVModal onClose={() => setCsvModal(false)} onDone={load} />}
    </div>
  );
}

// ─── Stock Tab ───────────────────────────────────────────────────────────────
function StockTab() {
  const { isAdmin } = useAuth();
  const [summary,  setSummary]  = useState([]);
  const [alerts,   setAlerts]   = useState([]);
  const [minimums, setMinimums] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [types,    setTypes]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [minModal, setMinModal] = useState(false);
  const [mForm,    setMForm]    = useState({ campus_id:'', asset_type:'', min_count:'' });
  const [busy,     setBusy]     = useState(false);
  const [consumables, setConsumables] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, cn] = await Promise.all([
        api.get('/assets/stock/summary'),
        api.get('/assets/stock/alerts'),
        api.get('/consumables'),
      ]);
      setSummary(s.data); setAlerts(a.data); setConsumables(cn.data);
      if (isAdmin) {
        const [m] = await Promise.all([ api.get('/assets/stock/minimums') ]);
        setMinimums(m.data);
      }
    } catch(e) {}
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isAdmin) {
      api.get('/master/campuses').then(r => setCampuses(r.data));
      api.get('/master/device-types').then(r => setTypes(r.data.filter(t => t.active).map(t => t.name).sort()));
    }
  }, [isAdmin]);

  // Group summary rows by brand+model
  const grouped = (() => {
    const map = {};
    summary.forEach(r => {
      const k = `${r.brand}||${r.model}`;
      if (!map[k]) map[k] = { brand: r.brand, model: r.model, total: 0, byCampus: [] };
      map[k].total += r.count;
      map[k].byCampus.push({ name: r.campus_name, count: r.count });
    });
    return Object.values(map).sort((a,b) => (a.brand+a.model).localeCompare(b.brand+b.model));
  })();

  const saveMin = async () => {
    if (!mForm.campus_id || !mForm.asset_type) return;
    setBusy(true);
    try {
      await api.post('/assets/stock/minimums', { campus_id: parseInt(mForm.campus_id), asset_type: mForm.asset_type, min_count: parseInt(mForm.min_count)||0 });
      setMForm({ campus_id:'', asset_type:'', min_count:'' });
      load();
    } catch(e) {} finally { setBusy(false); }
  };
  const delMin = async id => { await api.delete(`/assets/stock/minimums/${id}`); load(); };

  return (
    <div>
      {/* Low-stock notification */}
      {alerts.length > 0 && (
        <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:'var(--radius)', padding:'12px 16px', marginBottom:16 }}>
          <div style={{ fontWeight:600, color:'var(--red)', fontSize:13, marginBottom:8 }}>⚠ Low Stock — {alerts.length} item{alerts.length>1?'s':''} below minimum</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {alerts.map((al,i) => (
              <span key={i} style={{ fontSize:12, padding:'3px 10px', borderRadius:12, background:'rgba(248,113,113,0.12)', color:'var(--red)', border:'1px solid rgba(248,113,113,0.25)' }}>
                {isAdmin && <strong>{al.campus_name}: </strong>}{al.asset_type} — {al.current}/{al.min_count}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:16 }}>
        <div style={{ fontSize:13, color:'var(--text2)' }}>
          Assets currently in stock{isAdmin ? ' across all campuses' : ' at your campus'}.
        </div>
        {isAdmin && <button className="btn btn-secondary" onClick={() => setMinModal(true)}>⚙ Manage Minimums</button>}
      </div>

      {/* Stock table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !grouped.length ? <div className="empty-state"><h3>No stock</h3><p>New assets default to stock and will appear here.</p></div>
        : (
          <div className="table-wrap">
            <table>
              <thead>
                {isAdmin
                  ? <tr><th style={{ width:50 }}>S.No</th><th>Brand</th><th>Model</th><th>Campuses</th><th>Total</th></tr>
                  : <tr><th style={{ width:50 }}>S.No</th><th>Brand</th><th>Model</th><th>Count</th></tr>}
              </thead>
              <tbody>
                {grouped.map((g,i) => (
                  <tr key={i}>
                    <td style={{ color:'var(--text2)', fontSize:12 }}>{i+1}</td>
                    <td style={{ fontWeight:500 }}>{g.brand}</td>
                    <td style={{ color:'var(--text2)' }}>{g.model}</td>
                    {isAdmin ? (
                      <>
                        <td><span style={{ fontSize:12 }}>{g.byCampus.map(c => `${c.name} - ${c.count}`).join(', ')}</span></td>
                        <td><span style={{ fontWeight:600, color:'var(--accent)' }}>{g.total}</span></td>
                      </>
                    ) : (
                      <td><span style={{ fontWeight:600, color:'var(--accent)' }}>{g.total}</span></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Consumables stock — shown here too so all stock is in one place */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'24px 0 10px' }}>
        <div style={{ fontSize:13, fontWeight:600 }}>🧰 Consumables Stock</div>
        <span style={{ fontSize:11, color:'var(--text3)' }}>Manage in the Consumables tab</span>
      </div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!consumables.length ? <div className="empty-state"><p>No consumables yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th style={{ width:50 }}>S.No</th><th>Item</th><th>Category</th>{isAdmin && <th>Campus</th>}<th>Unit</th><th>Current</th><th>Min</th><th>Status</th></tr></thead>
            <tbody>
              {consumables.map((c,i) => (
                <tr key={c.id} style={{ background: c.low ? 'rgba(248,113,113,0.06)' : '' }}>
                  <td style={{ color:'var(--text2)', fontSize:12 }}>{i+1}</td>
                  <td style={{ fontWeight:500 }}>{c.name}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{c.category||'—'}</td>
                  {isAdmin && <td style={{ fontSize:12 }}>{c.campus_name}</td>}
                  <td style={{ fontSize:12 }}>{c.unit_type}</td>
                  <td><span style={{ fontWeight:600, color:'var(--accent)' }}>{(+c.current_stock).toFixed(2).replace(/\.00$/,'')}</span></td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{(+c.min_stock).toFixed(2).replace(/\.00$/,'')}</td>
                  <td>{c.low ? <span className="badge badge-faulty">Low</span> : <span className="badge badge-completed">OK</span>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Manage Minimums (admin) */}
      {minModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setMinModal(false)}>
          <div className="modal" style={{ maxWidth:560 }}>
            <div className="modal-header">
              <span className="modal-title">Minimum Stock Levels</span>
              <button className="modal-close" onClick={() => setMinModal(false)}>×</button>
            </div>
            <p style={{ fontSize:13, color:'var(--text2)', marginBottom:14 }}>
              Set the minimum stock to maintain per asset type per campus. A low-stock alert shows when stock falls below it.
            </p>
            {/* Add/update row */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 80px auto', gap:8, alignItems:'end', marginBottom:14 }}>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Campus</label>
                <select value={mForm.campus_id} onChange={e => setMForm(f => ({ ...f, campus_id: e.target.value }))}>
                  <option value="">—</option>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Asset Type</label>
                <select value={mForm.asset_type} onChange={e => setMForm(f => ({ ...f, asset_type: e.target.value }))}>
                  <option value="">—</option>
                  {types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Min</label>
                <input type="number" min={0} value={mForm.min_count} onChange={e => setMForm(f => ({ ...f, min_count: e.target.value }))} />
              </div>
              <button className="btn btn-primary btn-sm" onClick={saveMin} disabled={busy || !mForm.campus_id || !mForm.asset_type}>Set</button>
            </div>
            {/* Existing minimums */}
            <div className="table-wrap" style={{ maxHeight:300, overflowY:'auto' }}>
              <table>
                <thead><tr><th>Campus</th><th>Asset Type</th><th>Min</th><th>Current</th><th></th></tr></thead>
                <tbody>
                  {minimums.length === 0
                    ? <tr><td colSpan={5} style={{ textAlign:'center', color:'var(--text2)', padding:16 }}>No minimums set yet</td></tr>
                    : minimums.map(m => (
                      <tr key={m.id} style={{ background: m.current < m.min_count ? 'rgba(248,113,113,0.06)' : '' }}>
                        <td style={{ fontSize:12 }}>{m.campus_name}</td>
                        <td style={{ fontSize:12 }}>{m.asset_type}</td>
                        <td style={{ fontSize:12 }}>{m.min_count}</td>
                        <td><span style={{ fontSize:12, color: m.current < m.min_count ? 'var(--red)' : 'var(--green)', fontWeight:600 }}>{m.current}</span></td>
                        <td><button className="btn btn-danger btn-sm" onClick={() => delMin(m.id)}>×</button></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setMinModal(false)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Assets Page (tabs) ──────────────────────────────────────────────────────
// ─── Service Tab — full service lifecycle ─────────────────────────────────────
const SVC_STATUS = [
  ['sent_to_vendor','Sent to Vendor','badge-pending'],
  ['received_by_vendor','Received by Vendor','badge-discuss'],
  ['inspection_in_progress','Inspection in Progress','badge-discuss'],
  ['waiting_quotation','Waiting for Quotation','badge-repair'],
  ['quotation_received','Quotation Received','badge-discuss'],
  ['waiting_approval','Waiting for Approval','badge-repair'],
  ['quotation_approved','Quotation Approved','badge-completed'],
  ['repair_in_progress','Repair in Progress','badge-pending'],
  ['ready_for_collection','Ready for Collection','badge-discuss'],
  ['received_from_vendor','Received from Vendor','badge-discuss'],
  ['returned_to_stock','Returned to Stock','badge-completed'],
  ['scrap_recommended','Scrap Recommended','badge-faulty'],
];
const SVC_LABEL = Object.fromEntries(SVC_STATUS.map(([k,l]) => [k,l]));
const SVC_BADGE = Object.fromEntries(SVC_STATUS.map(([k,,b]) => [k,b]));
const PRIORITY_BADGE = { Critical:'badge-faulty', High:'badge-repair', Medium:'badge-normal', Low:'badge-discuss' };
const slaState = exp => {
  if (!exp) return null;
  const days = Math.ceil((new Date(exp) - new Date()) / 86400000);
  if (days < 0) return { dot:'🔴', label:`Breached ${-days}d`, color:'var(--red)' };
  if (days <= 1) return { dot:'🟡', label: days===0?'Due today':'Due tomorrow', color:'var(--amber)' };
  return { dot:'🟢', label:`${days}d left`, color:'var(--green)' };
};

function ServiceTab() {
  const { isAdmin, can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);   // { id, vendor_ticket_id, service_status, remarks }
  const [timeline, setTimeline] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quoteForm, setQuoteForm] = useState({ amount:'', quote_date:'', est_completion:'', vendor_remarks:'' });
  const [costForm, setCostForm] = useState({ service_charge:'', spare_parts_cost:'', labour_cost:'', transport_cost:'' });
  const [costTotal, setCostTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/assets/service/list'); setRows(r.data); } catch(e) {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const editable = can ? can('assets','edit') : isAdmin;
  const PRIO_ORDER = { Critical:0, High:1, Medium:2, Low:3 };
  const sorted = [...rows].sort((a,b) => (PRIO_ORDER[a.priority]??2) - (PRIO_ORDER[b.priority]??2));

  const loadSub = async id => {
    try { const r = await api.get(`/assets/${id}/service-timeline`); setTimeline(r.data); } catch(e) {}
    try { const r = await api.get(`/assets/${id}/quotations`); setQuotes(r.data); } catch(e) {}
    try { const r = await api.get(`/assets/${id}/service-cost-history`); setCostTotal(r.data.total_since_purchase||0); } catch(e) {}
  };
  const openEdit = async a => {
    setErr(''); setTimeline([]); setQuotes([]); setCostTotal(0);
    setQuoteForm({ amount:'', quote_date:'', est_completion:'', vendor_remarks:'' });
    setCostForm({ service_charge:'', spare_parts_cost:'', labour_cost:'', transport_cost:'' });
    setEdit({ id:a.id, asset_tag:a.asset_tag, vendor_ticket_id:a.vendor_ticket_id||'', service_status:a.service_status||'sent_to_vendor', remarks:'' });
    loadSub(a.id);
  };
  const addQuote = async () => {
    if (!quoteForm.amount) { setErr('Enter quotation amount'); return; }
    setBusy(true); setErr('');
    try { await api.post(`/assets/${edit.id}/quotation`, quoteForm); setQuoteForm({ amount:'', quote_date:'', est_completion:'', vendor_remarks:'' }); loadSub(edit.id); load(); }
    catch(e){ setErr(e.response?.data?.error||'Failed'); } finally { setBusy(false); }
  };
  const decideQuote = async (qid, decision) => {
    const remarks = decision==='Rejected' ? (window.prompt('Reason for rejection (optional):')||'') : '';
    setBusy(true);
    try { await api.put(`/assets/quotation/${qid}/decision`, { decision, remarks }); loadSub(edit.id); load(); }
    catch(e){ setErr(e.response?.data?.error||'Failed'); } finally { setBusy(false); }
  };
  const saveCost = async () => {
    setBusy(true); setErr('');
    try { await api.put(`/assets/${edit.id}/service-cost`, costForm); loadSub(edit.id); }
    catch(e){ setErr(e.response?.data?.error||'Failed'); } finally { setBusy(false); }
  };
  const costSum = ['service_charge','spare_parts_cost','labour_cost','transport_cost']
    .reduce((s,k) => s + (parseFloat(costForm[k])||0), 0);
  const save = async () => {
    if (edit.service_status === 'scrap_recommended' &&
        !window.confirm('Scrap Recommended will retire this asset and close the service. Continue?')) return;
    if (edit.service_status === 'returned_to_stock' &&
        !window.confirm('Returning to stock will close this service and make the asset available. Continue?')) return;
    setBusy(true); setErr('');
    try {
      await api.put(`/assets/${edit.id}/service-status`, { vendor_ticket_id: edit.vendor_ticket_id || null, service_status: edit.service_status, remarks: edit.remarks || null });
      setEdit(null); load();
    } catch(e) { setErr(e.response?.data?.error || 'Update failed'); } finally { setBusy(false); }
  };

  return (
    <div>
      <p style={{ fontSize:13, color:'var(--text2)', marginBottom:14 }}>
        Assets currently under service, sorted by priority. Update the status to advance the lifecycle — every change is recorded on the asset's permanent timeline.
      </p>
      {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
      : !sorted.length ? <div className="empty-state"><p>No items are under service.</p></div>
      : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap"><table>
            <thead><tr>
              <th>Asset</th><th>Campus</th><th>Vendor</th><th>Priority</th><th>Status</th><th>SLA</th><th>Sent</th>{editable && <th></th>}
            </tr></thead>
            <tbody>
              {sorted.map(a => {
                const sla = slaState(a.expected_return_date);
                return (
                <tr key={a.id}>
                  <td><div style={{ fontWeight:500 }}>{a.brand} {a.model}</div>
                    <div style={{ fontSize:11, color:'var(--text2)' }} className="mono">{a.asset_tag} · {a.asset_type}</div>
                    {a.problem_desc && <div style={{ fontSize:11, color:'var(--text2)' }}>{a.problem_desc}</div>}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{a.campus_name}</td>
                  <td style={{ fontSize:12 }}>{a.vendor || '—'}{a.vendor_ticket_id && <div style={{ fontSize:11, color:'var(--text2)' }} className="mono">Job: {a.vendor_ticket_id}</div>}</td>
                  <td><span className={`badge ${PRIORITY_BADGE[a.priority]||'badge-normal'}`}>{a.priority||'Medium'}</span></td>
                  <td><span className={`badge ${SVC_BADGE[a.service_status]||'badge-pending'}`}>{SVC_LABEL[a.service_status]||'Sent to Vendor'}</span></td>
                  <td style={{ fontSize:12 }}>{sla ? <span style={{ color:sla.color }}>{sla.dot} {sla.label}</span> : '—'}
                    {a.expected_return_date && <div style={{ fontSize:11, color:'var(--text2)' }}>exp {new Date(a.expected_return_date).toLocaleDateString()}</div>}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{a.service_out_date ? new Date(a.service_out_date).toLocaleDateString() : '—'}</td>
                  {editable && <td><button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}>Update</button></td>}
                </tr>
              );})}
            </tbody>
          </table></div>
        </div>
      )}

      {edit && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setEdit(null)}>
          <div className="modal" style={{ maxWidth:540, maxHeight:'88vh', overflowY:'auto' }}>
            <div className="modal-header"><span className="modal-title">Update Service — <span className="mono" style={{ color:'var(--accent)' }}>{edit.asset_tag}</span></span><button className="modal-close" onClick={() => setEdit(null)}>×</button></div>
            <div className="form-row">
              <div className="form-group"><label>Service Status</label>
                <select value={edit.service_status} onChange={e => setEdit(s => ({ ...s, service_status:e.target.value }))}>
                  {SVC_STATUS.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
                </select></div>
              <div className="form-group"><label>Vendor Job / Ref No.</label>
                <input value={edit.vendor_ticket_id} onChange={e => setEdit(s => ({ ...s, vendor_ticket_id:e.target.value }))} placeholder="vendor's reference" /></div>
            </div>
            <div className="form-group"><label>Remarks</label>
              <input value={edit.remarks} onChange={e => setEdit(s => ({ ...s, remarks:e.target.value }))} placeholder="note for the timeline" /></div>

            {/* Timeline */}
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'8px 0 6px' }}>Timeline</div>
            <div style={{ maxHeight:180, overflowY:'auto', borderLeft:'2px solid var(--border)', paddingLeft:12 }}>
              {!timeline.length ? <div style={{ fontSize:12, color:'var(--text2)' }}>No events yet.</div>
              : timeline.map(ev => (
                <div key={ev.id} style={{ marginBottom:10 }}>
                  <div style={{ fontSize:12, fontWeight:600 }}>{SVC_LABEL[ev.status]||ev.status}</div>
                  <div style={{ fontSize:11, color:'var(--text2)' }}>{new Date(ev.created_at).toLocaleString()} · {ev.by_name||'—'}</div>
                  {ev.remarks && <div style={{ fontSize:12 }}>{ev.remarks}</div>}
                </div>
              ))}
            </div>

            {/* Quotation */}
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'14px 0 6px', borderTop:'1px solid var(--border)', paddingTop:12 }}>Quotation</div>
            {quotes.map(q => (
              <div key={q.id} style={{ fontSize:12, padding:'6px 10px', background:'var(--bg3)', borderRadius:6, marginBottom:6, display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                <div>
                  <b>₹{q.amount}</b> · {q.quote_date ? new Date(q.quote_date).toLocaleDateString() : '—'}
                  {q.est_completion && <span style={{ color:'var(--text2)' }}> · est {new Date(q.est_completion).toLocaleDateString()}</span>}
                  {q.vendor_remarks && <div style={{ color:'var(--text2)' }}>{q.vendor_remarks}</div>}
                  <span className={`badge ${q.approval_status==='Approved'?'badge-completed':q.approval_status==='Rejected'?'badge-faulty':'badge-pending'}`} style={{ marginTop:2 }}>{q.approval_status}</span>
                  {q.decided_by_name && <span style={{ color:'var(--text2)', marginLeft:6 }}>by {q.decided_by_name}</span>}
                </div>
                {isAdmin && q.approval_status==='Pending' && (
                  <div className="flex-gap" style={{ gap:4 }}>
                    <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => decideQuote(q.id,'Approved')}>Approve</button>
                    <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => decideQuote(q.id,'Rejected')}>Reject</button>
                  </div>
                )}
              </div>
            ))}
            <div className="form-row">
              <div className="form-group"><label>Quote Amount (₹)</label><input type="number" min="0" value={quoteForm.amount} onChange={e=>setQuoteForm(f=>({...f,amount:e.target.value}))} /></div>
              <div className="form-group"><label>Quote Date</label><input type="date" value={quoteForm.quote_date} onChange={e=>setQuoteForm(f=>({...f,quote_date:e.target.value}))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Est. Completion</label><input type="date" value={quoteForm.est_completion} onChange={e=>setQuoteForm(f=>({...f,est_completion:e.target.value}))} /></div>
              <div className="form-group"><label>Vendor Remarks</label><input value={quoteForm.vendor_remarks} onChange={e=>setQuoteForm(f=>({...f,vendor_remarks:e.target.value}))} /></div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={addQuote} disabled={busy}>+ Add Quotation</button>

            {/* Cost breakdown */}
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'14px 0 6px', borderTop:'1px solid var(--border)', paddingTop:12 }}>Service Cost</div>
            <div className="form-row">
              <div className="form-group"><label>Service Charge (₹)</label><input type="number" min="0" value={costForm.service_charge} onChange={e=>setCostForm(f=>({...f,service_charge:e.target.value}))} /></div>
              <div className="form-group"><label>Spare Parts (₹)</label><input type="number" min="0" value={costForm.spare_parts_cost} onChange={e=>setCostForm(f=>({...f,spare_parts_cost:e.target.value}))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Labour (₹)</label><input type="number" min="0" value={costForm.labour_cost} onChange={e=>setCostForm(f=>({...f,labour_cost:e.target.value}))} /></div>
              <div className="form-group"><label>Transport (₹)</label><input type="number" min="0" value={costForm.transport_cost} onChange={e=>setCostForm(f=>({...f,transport_cost:e.target.value}))} /></div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13 }}>
              <span>Total this service: <b>₹{costSum.toFixed(2)}</b> · Since purchase: <b>₹{Number(costTotal).toFixed(2)}</b></span>
              <button className="btn btn-secondary btn-sm" onClick={saveCost} disabled={busy}>Save Cost</button>
            </div>

            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEdit(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Update Status'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Assets() {
  const [tab, setTab] = useState('it');
  return (
    <div>
      <div style={{ display:'flex', gap:0, marginBottom:20, borderBottom:'1px solid var(--border)' }}>
        {[['it','🖥 IT Assets'],['stock','📦 Stock'],['student','🎓 Student Laptops'],['consumables','🧰 Consumables'],['service','🔧 Service'],['scrap','🗑 Scrap']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'9px 22px', fontSize:13, fontFamily:'var(--font)', cursor:'pointer', background:'transparent', border:'none',
              borderBottom: tab===id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab===id ? 'var(--accent)' : 'var(--text2)', fontWeight: tab===id ? 600 : 400, marginBottom:-1 }}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'it' ? <AssetRegister /> : tab === 'stock' ? <StockTab /> : tab === 'student' ? <StudentLaptops />
        : tab === 'consumables' ? <Consumables embedded /> : tab === 'service' ? <ServiceTab /> : <Scrap />}
    </div>
  );
}
